import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Trophy } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function PRs() {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prs");
        setPrs(data);
        if (data.length > 0) setSelected(data[0].exercise);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      const { data } = await api.get(`/prs/progress?exercise=${encodeURIComponent(selected)}`);
      setChartData(data.map((d) => ({ ...d, date: new Date(d.date).toLocaleDateString() })));
    })();
  }, [selected]);

  return (
    <div className="space-y-8" data-testid="prs-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono">// Personal Records</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase">Your PRs</h1>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : prs.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center">
          <Trophy size={40} weight="bold" className="text-zinc-700 mx-auto mb-4" />
          <div className="font-display text-2xl font-bold uppercase text-zinc-400 mb-2">No PRs yet</div>
          <p className="text-zinc-500 text-sm">Log a workout to start tracking personal records.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          {/* PR list */}
          <div className="border border-[#27272A] bg-[#121212] max-h-[70vh] overflow-y-auto">
            {prs.map((p) => (
              <button
                key={p.exercise}
                onClick={() => setSelected(p.exercise)}
                data-testid={`pr-item-${p.exercise.replace(/\s+/g, "-")}`}
                className={`w-full text-left p-4 border-b border-[#27272A] transition-colors flex items-center justify-between ${
                  selected === p.exercise ? "bg-[#1E1E1E] border-l-2 border-l-[#FF3B30]" : "hover:bg-[#1E1E1E]"
                }`}
              >
                <div>
                  <div className="font-display text-lg font-bold uppercase tracking-tight capitalize">{p.exercise}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black text-[#FF3B30]">{p.weight}</div>
                  <div className="text-[10px] uppercase text-zinc-500 font-mono">kg</div>
                </div>
              </button>
            ))}
          </div>

          {/* Chart */}
          <div className="border border-[#27272A] bg-[#121212] p-6">
            {selected ? (
              <>
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Progress</div>
                  <div className="font-display text-3xl font-black uppercase capitalize">{selected}</div>
                </div>
                {chartData.length < 2 ? (
                  <div className="text-zinc-500 text-sm">Need at least 2 sessions with this exercise to plot progress.</div>
                ) : (
                  <div style={{ height: 320 }}>
                    <ResponsiveContainer>
                      <LineChart data={chartData}>
                        <CartesianGrid stroke="#27272A" vertical={false} />
                        <XAxis dataKey="date" stroke="#52525b" tick={{ fill: "#52525b", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                        <YAxis stroke="#52525b" tick={{ fill: "#52525b", fontSize: 11, fontFamily: "JetBrains Mono" }} />
                        <Tooltip
                          contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 0, color: "#fff" }}
                          labelStyle={{ color: "#A1A1AA" }}
                        />
                        <Line type="monotone" dataKey="weight" stroke="#FF3B30" strokeWidth={2} dot={{ r: 4, fill: "#FF3B30" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : (
              <div className="text-zinc-500 text-sm">Select an exercise to view progress</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
